/* SG Header: scroll state, autohide, header height, full-screen menu. No dependencies.
   Everything registered on window/document is tied to an AbortController, so
   a theme-editor reload or unload of the header tears it down cleanly. */
(function () {
  'use strict';

  var current = null; // { controller, menu } for the live header instance

  function destroy() {
    if (!current) return;
    current.controller.abort();
    if (current.observer) current.observer.disconnect();
    if (current.menu && current.menu.isConnected) current.menu.remove();
    document.documentElement.classList.remove('sg-menu-lock');
    current = null;
  }

  function init(root) {
    var wrap = root.querySelector('[data-sg-header]');
    if (!wrap || wrap.dataset.sgReady) return;
    destroy();
    wrap.dataset.sgReady = '1';

    var controller = new AbortController();
    var signal = controller.signal;
    current = { controller: controller };

    var section = wrap.closest('.shopify-section') || wrap;
    var html = document.documentElement;
    var autohide = wrap.classList.contains('sg-header-wrap--autohide');
    var bar = wrap.querySelector('.sg-header');

    /* ---------- Height (the hero pulls itself up under the header) ---------- */
    function setHeight() {
      html.style.setProperty('--sg-header-h', bar.offsetHeight + 'px');
    }
    setHeight();
    if (window.ResizeObserver) {
      current.observer = new ResizeObserver(setHeight);
      current.observer.observe(bar);
    }

    /* ---------- Scroll: hide going down, show going up ---------- */
    var lastY = window.scrollY, ticking = false;
    function onScroll() {
      ticking = false;
      var y = window.scrollY;
      section.classList.toggle('is-scrolled', y > 10);
      if (autohide && !html.classList.contains('sg-menu-lock')) {
        var down = y > lastY + 2;
        var up = y < lastY - 2;
        if (down && y > 160 && !section.contains(document.activeElement)) section.classList.add('is-hidden');
        else if (up || y <= 160) section.classList.remove('is-hidden');
      }
      lastY = y;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(onScroll); }
    }, { passive: true, signal: signal });
    section.addEventListener('focusin', function () { section.classList.remove('is-hidden'); });

    /* ---------- Menu ---------- */
    var menu = wrap.querySelector('[data-sg-menu]');
    if (!menu) return;
    // Escape the sticky header's stacking context. Drop any menu left behind
    // by a previous header instance first.
    document.querySelectorAll('body > [data-sg-menu]').forEach(function (old) { if (old !== menu) old.remove(); });
    document.body.appendChild(menu);
    current.menu = menu;

    var openers = wrap.querySelectorAll('[data-sg-menu-open]');
    var closeBtn = menu.querySelector('[data-sg-menu-close]');
    var items = menu.querySelectorAll('[data-sg-menu-item]');
    var desktop = window.matchMedia('(min-width: 990px)');
    var lastFocus = null;

    function isOpen() { return menu.classList.contains('is-open'); }

    function setOpen(on) {
      if (on === isOpen()) return;
      menu.classList.toggle('is-open', on);
      if (on) menu.removeAttribute('inert'); else menu.setAttribute('inert', '');
      html.classList.toggle('sg-menu-lock', on); // lock on <html> only (see section-sg-header.css)
      openers.forEach(function (b) { b.setAttribute('aria-expanded', on ? 'true' : 'false'); });
      if (on) {
        // Safari doesn't focus buttons on click: fall back to the Menu button
        lastFocus = document.activeElement && document.activeElement !== document.body ? document.activeElement : openers[0];
        setTimeout(function () { (menu.querySelector('.sg-menu__l1-link') || closeBtn).focus({ preventScroll: true }); }, 60);
      } else if (lastFocus && lastFocus.focus) {
        lastFocus.focus({ preventScroll: true });
      }
    }

    openers.forEach(function (b) { b.addEventListener('click', function () { setOpen(true); }); });
    if (closeBtn) closeBtn.addEventListener('click', function () { setOpen(false); });

    // Escape closes the menu wherever focus is (clicking empty space in the
    // menu moves focus to <body>, so a listener on the menu alone would miss it)
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) setOpen(false);
    }, { signal: signal });

    // Keep Tab inside the open menu
    menu.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var f = Array.prototype.filter.call(menu.querySelectorAll('a[href], button'), function (el) {
        return el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden';
      });
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    });

    // Desktop: hovering / focusing a level-1 item shows its panel.
    // Mobile: its chevron toggles the accordion. The title is a link either way.
    function activate(item) {
      items.forEach(function (it) {
        var on = it === item;
        it.classList.toggle('is-active', on);
        var btn = it.querySelector('.sg-menu__toggle');
        if (btn && desktop.matches) btn.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
    }

    function closeAccordions(except) {
      items.forEach(function (it) {
        if (it === except) return;
        it.classList.remove('is-open');
        var b = it.querySelector('.sg-menu__toggle');
        if (b && !desktop.matches) b.setAttribute('aria-expanded', 'false');
      });
    }

    items.forEach(function (item) {
      var link = item.querySelector('.sg-menu__l1-link');
      var btn = item.querySelector('.sg-menu__toggle');
      item.addEventListener('pointerenter', function () { if (desktop.matches) activate(item); });
      if (link) link.addEventListener('focus', function () { if (desktop.matches) activate(item); });
      if (btn) {
        btn.addEventListener('click', function () {
          if (desktop.matches) { activate(item); return; }
          var open = !item.classList.contains('is-open');
          closeAccordions(item);
          item.classList.toggle('is-open', open);
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }
    });

    // Switching layouts: reset the state the other layout used
    desktop.addEventListener('change', function () {
      closeAccordions(null);
      var active = menu.querySelector('[data-sg-menu-item].is-active') || items[0];
      if (desktop.matches && active) activate(active);
    }, { signal: signal });
  }

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target.querySelector('[data-sg-header]')) destroy();
  });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
})();
